/**
 * The clinic's own brand on the clinic admin app (M17.3, FR-UI-03).
 *
 * The admin app used to wear the platform's blue for every clinic. It now applies the
 * tenant's branding at runtime with the **same** `applyTenantBranding` the patient app
 * uses (`@platform/ui`), so a clinic's staff and its patients see one brand — including
 * the logo in the nav (`<Logo variant="tenant" />` already reads `--logo-url`) and the
 * favicon. The super console keeps the platform theme: it serves every clinic, so no
 * clinic's colours belong on it.
 *
 * ## Why `GET /public/tenant` and not the settings read
 *
 * `GET /admin/branding` and `GET /admin/settings` need `tenant.branding.manage` /
 * `tenant.settings.manage`, which a front-desk account does not hold — theming from them
 * would brand the app for the clinic admin and nobody else. `GET /public/tenant` answers
 * for whichever clinic the **host** resolved to (the admin host resolves to its tenant as
 * the patient host does), needs no permission, carries only public configuration, and is
 * cached for sixty seconds (`private, max-age=60`), so it costs one small request per
 * sixty seconds of a session. It never decides anything: a failed read leaves the
 * platform theme in place and says nothing.
 *
 * A save on the settings page re-applies the saved branding at once
 * (`settings-screen.tsx`), so the clinic admin sees their change without waiting out
 * the cache.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import type { PublicTenant } from '@platform/api-client';
import { useApiClient } from '@platform/api-client/react';
import { applyDocumentBranding, applyTenantBranding, type TenantThemeInput } from '@platform/ui';

export const ADMIN_TENANT_BRANDING_KEY = ['admin-tenant-branding'] as const;

/** Paints `branding` onto `:root` and the favicon into `<head>`. */
export function applyClinicBranding(
  branding: TenantThemeInput & { clinicName: string; faviconUrl: string | null },
): void {
  applyTenantBranding(branding);
  applyDocumentBranding({
    clinicName: branding.clinicName,
    faviconUrl: branding.faviconUrl,
    brandVersion: branding.brandVersion,
  });
}

/** Reads the host's branding and applies it. Renders nothing; mount it once in the shell. */
export function useClinicBranding(): void {
  const api = useApiClient();
  const tenant = useQuery<PublicTenant>({
    queryKey: ADMIN_TENANT_BRANDING_KEY,
    queryFn: ({ signal }) => api.getPublicTenant({ signal }),
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    if (tenant.data === undefined) return;
    applyClinicBranding(tenant.data.branding);
  }, [tenant.data]);
}
